import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaStar, FaRegStar } from "react-icons/fa";
import { Quote } from "lucide-react";
import api from "../../../configs/api";

// Star Rating Component
const StarRating = ({ rating, size = 16 }) => {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) =>
        i < rating ? (
          <FaStar key={i} size={size} className="text-yellow-400" />
        ) : (
          <FaRegStar key={i} size={size} className="text-yellow-300/50" />
        ),
      )}
    </div>
  );
};

// Single Review Card
const ReviewCard = ({ review }) => {
  return (
    <div className="w-[280px] sm:w-[300px] md:w-[320px] flex-shrink-0 bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 overflow-hidden group">
      {/* Top accent based on rating */}
      <div
        className="h-1.5 w-full"
        style={{
          background:
            review.rating >= 4
              ? "linear-gradient(90deg, #a00984, #a3228b)"
              : review.rating === 3
                ? "linear-gradient(90deg, #94a3b8, #cbd5e1)"
                : "linear-gradient(90deg, #f87171, #ef4444)",
        }}
      />

      <div className="p-5 flex flex-col gap-3">
        {/* Rating Stars */}
        <StarRating rating={review.rating} size={18} />

        {/* Review Text */}
        <p className="text-gray-600 text-sm leading-relaxed line-clamp-4 min-h-[80px]">
          “{review.description}”
        </p>

        {/* Divider */}
        <div className="border-t border-gray-100 my-1" />

        {/* Name + Status */}
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#cbd5e1] flex items-center justify-center text-primary-700 text-xs font-bold">
              {review.name.charAt(0).toUpperCase()}
            </div>
            <h4 className="font-semibold text-gray-800 text-sm truncate max-w-[130px]">
              {review.name}
            </h4>
          </div>
          <span
            className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
              review.status === "active"
                ? "bg-green-50 text-green-600 border border-green-200"
                : "bg-gray-100 text-gray-400 border border-gray-200"
            }`}
          >
            {review.status === "active" ? "Verified" : "Inactive"}
          </span>
        </div>
      </div>
    </div>
  );
};

// Loading Skeleton
const SkeletonCard = () => (
  <div className="w-[280px] sm:w-[300px] md:w-[320px] flex-shrink-0 bg-white rounded-2xl shadow-md border border-gray-100 animate-pulse overflow-hidden">
    <div className="h-1.5 bg-gray-200 w-full" />
    <div className="p-5 space-y-3">
      <div className="flex gap-1">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="w-4 h-4 bg-gray-200 rounded-sm" />
        ))}
      </div>
      <div className="space-y-2">
        <div className="h-3 bg-gray-200 rounded w-full" />
        <div className="h-3 bg-gray-200 rounded w-11/12" />
        <div className="h-3 bg-gray-200 rounded w-10/12" />
        <div className="h-3 bg-gray-200 rounded w-9/12" />
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-gray-200 rounded-full" />
          <div className="h-3 bg-gray-200 rounded w-20" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-14" />
      </div>
    </div>
  </div>
);

// Empty State
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <Quote size={28} className="text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-700">No reviews yet</h3>
    <p className="text-gray-400 text-sm mt-1">
      Be the first to share your experience
    </p>
  </div>
);

// Main Infinite Scroll Review Marquee
const InfiniteReviewMarquee = ({ autoScrollSpeed = 30 }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchReviews = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/reviews", { signal: controller.signal });

      // Extract reviews array from possible API structures
      let reviewList = [];
      if (response.data?.data) {
        reviewList = Array.isArray(response.data.data)
          ? response.data.data
          : response.data.data?.reviews || [];
      } else if (Array.isArray(response.data)) {
        reviewList = response.data;
      } else if (response.data?.reviews) {
        reviewList = response.data.reviews;
      }

      // Filter only active reviews and sort newest first
      const activeReviews = reviewList
        .filter((review) => review.status === "active")
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setReviews(activeReviews);
    } catch (err) {
      if (err.name !== "CanceledError" && err.name !== "AbortError") {
        console.error("Failed to fetch reviews:", err);
        setError("Unable to load reviews. Please refresh the page.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
    return () => abortRef.current?.abort();
  }, [fetchReviews]);

  // Create triple array for seamless looping (like partners component)
  const allReviews =
    reviews.length > 0 ? [...reviews, ...reviews, ...reviews] : [];

  // Loading UI
  if (loading) {
    return (
      <div className="w-full bg-gray-50 py-12 overflow-hidden">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
            Customer Reviews
          </h2>
          <p className="text-gray-500 text-sm mt-1">What our customers say</p>
        </div>
        <div className="relative overflow-hidden">
          <div className="flex gap-5 px-5">
            {[...Array(6)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-gray-50 py-12 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Customer Reviews
        </h2>
        <p className="text-red-500">{error}</p>
        <button
          onClick={fetchReviews}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="w-full bg-gray-50 py-12">
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
            Customer Reviews
          </h2>
          <p className="text-gray-500 text-sm mt-1">What our customers say</p>
        </div>
        <EmptyState />
      </div>
    );
  }

  return (
    <div
      id="reviews-section"
      className="w-full py-10 lg:pb-16 px-4 sm:px-6 md:px-8 lg:px-10 overflow-hidden"
    >
      {/* Section Header */}
      <div className="text-center mb-8 ">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
          Customer Reviews
        </h2>
        <p className="text-gray-500 text-sm md:text-base mt-2 max-w-2xl mx-auto px-4">
          Real experiences from our valued customers
        </p>
      </div>

      {/* Marquee Container */}
      <div className="relative w-full overflow-hidden">
        {/* Left fade gradient */}
        <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />

        {/* Right fade gradient */}
        <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none" />

        {/* Scrolling Track */}
        <div
          className="flex gap-5 md:gap-6 w-max animate-scroll-right-to-left hover:[animation-play-state:paused]"
          style={{ animationDuration: `${autoScrollSpeed}s` }}
        >
          {allReviews.map((review, idx) => (
            <ReviewCard key={`${review._id}-${idx}`} review={review} />
          ))}
        </div>
      </div>

      {/* Custom CSS Animation */}
      <style>{`
        @keyframes scrollRightToLeft {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
        .animate-scroll-right-to-left {
          animation: scrollRightToLeft linear infinite;
        }
      `}</style>
    </div>
  );
};

export default InfiniteReviewMarquee;
